// =============================================================================
// demo.js — public sales-demo mode for demo.sevric.com
// -----------------------------------------------------------------------------
// When DEMO is on, the app skips Firebase auth and serves a seeded fleet so the
// REAL recon app (this exact build) can be shown publicly with no login. It is
// the production UI, just fed local data.
//
// BUILD-TIME ONLY. demo.sevric.com is produced by:
//   VITE_DEMO=1 npx vite build --outDir ../../demo-sevric-com --emptyOutDir
// so the constant is baked true in that bundle and false everywhere else.
//
// There are deliberately NO runtime triggers. A hostname sniff or a ?demo query
// param would ship a no-login admin path inside the recon.sevric.com bundle,
// reachable on any origin. Do not reintroduce them.
// =============================================================================
import { SEED_VEHICLES } from '../data/seedVehicles.js';

export const DEMO = import.meta.env?.VITE_DEMO === '1';

export const DEMO_PROFILE = {
  uid: 'demo-user',
  email: 'demo@sevric.com',
  name: 'Andrew Monahan',
  role: 'admin',
  tenantId: 'demo',
  storeId: 'demo-store',
  portalManaged: true,
};

const COLORS = ['Alpine White', 'Black Sapphire', 'Nightfall Blue', 'Silver Sky',
  'Graphite', 'Calypso Red', 'Blueprint', 'Titanium', 'Platinum White'];
function vinFor(id) {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619); }
  const base = (h >>> 0).toString(36) + ((Math.imul(h, 7) >>> 0)).toString(36);
  return (base + '00000000000000000').toUpperCase().replace(/[^A-Z0-9]/g, '0').slice(0, 17);
}

// Real vehicle shape the app expects. Seed cars already carry stock/year/make/
// model/trim/bodyType/currentStage/assignedToName/reconSpend/projectedGross —
// we add the fields the detail + title surfaces read, and convert the JS-Date
// stageEnteredAt to epoch-ms (what toMs/ageBucket expect).
export const DEMO_VEHICLES = SEED_VEHICLES.map((v, i) => ({
  ...v,
  tenantId: 'demo',
  storeId: 'demo-store',
  stageEnteredAt: v.stageEnteredAt instanceof Date ? v.stageEnteredAt.getTime() : v.stageEnteredAt,
  vin: vinFor(v.id),
  mileage: 12000 + ((i * 4337) % 46000),
  color: COLORS[i % COLORS.length],
  titleStatus: i % 5 === 0 ? 'pending' : 'in-hand',
  openRecalls: i % 6 === 0 ? 1 : 0,
  acquisitionSource: v.flag && /trade/i.test(v.flag) ? 'trade-in'
    : v.flag && /auction/i.test(v.flag) ? 'auction' : 'trade-in',
  reconSpendApproved: Math.round((v.reconSpend || 0) * 0.8),
  partsHolds: v.flagTone === 'hold'
    ? [{ id: 'h1', partName: 'OEM part on order', vendorId: 'v-oem', orderedAt: Date.now() - 2 * 864e5, etaAt: Date.now() + 2 * 864e5, status: 'ordered' }]
    : [],
  kickbacks: [],
}));

export const DEMO_VENDORS = [
  { id: 'v-oem', tenantId: 'demo', name: 'OEM Parts Direct', services: ['parts'], active: true },
  { id: 'v-eagle', tenantId: 'demo', name: 'Eagle Body Shop', services: ['body', 'paint'], active: true },
  { id: 'v-shine', tenantId: 'demo', name: 'ShineWorks Detail', services: ['detail'], active: true },
  { id: 'v-glass', tenantId: 'demo', name: 'Clear View Glass', services: ['glass'], active: true },
];

export const DEMO_LEDGER = DEMO_VEHICLES.slice(0, 12).map((v, i) => ({
  id: 'l' + i, tenantId: 'demo', storeId: 'demo-store', vehicleId: v.id,
  type: ['parts', 'labor', 'detail'][i % 3], description: 'Recon line',
  amount: v.reconSpend || 0, status: i % 3 === 0 ? 'approved' : 'draft',
  at: Date.now() - (i + 1) * 3600e3,
}));

export function demoVehicle(id) {
  return DEMO_VEHICLES.find((v) => v.id === id) || DEMO_VEHICLES[0];
}
